import { useEventListener, useMemoizedFn, useSize } from 'ahooks';
import { debounce } from 'lodash-es';
import { CSSProperties, ReactNode, RefObject, useLayoutEffect, useRef, useState } from 'react';

type VirtualItem = {
	width: number;
	height: number;
};
interface ColumnVirtualListProps<T extends VirtualItem> {
	/** 滚动容器的引用 */
	scrollContainerRef?: RefObject<HTMLDivElement> | HTMLElement;
	/** 离视窗多远会被卸载，默认是2000px */
	offsetHeight?: number;
	/** 数据源，必须 extends { width:number, height:number } */
	originalList: T[];
	style?: CSSProperties;
	className?: string;
	/** 不知道*/
	expandHeight?: number;
	/** 渲染列宽 */
	renderedColumnWidth: number;
	itemRender: (data: T, index: number) => ReactNode;
}

export function ColumnVirtualList<T extends { width: number; height: number }>(
	props: ColumnVirtualListProps<T>,
) {
	const {
		className,
		style,
		offsetHeight = 2000,
		originalList,
		scrollContainerRef,
		expandHeight = 0,
		renderedColumnWidth,
		itemRender,
	} = props;
	const contentRef = useRef<HTMLDivElement>(null);
	const contentSize = useSize(contentRef);
	const [range, setRange] = useState({ start: 0, end: originalList.length - 1 });

	const getRange = useMemoizedFn((scrollContent?: HTMLElement) => {
		if (!contentSize?.width || !scrollContent) return;

		const { scrollTop, clientHeight } = scrollContent;

		let start = 0;
		let currentHeight = offsetHeight;
		for (start = 0; start < originalList.length; start++) {
			const item = originalList[start];
			const aspectRatio = item.height / item.width;
			const height = aspectRatio * contentSize.width + 20 + expandHeight;
			currentHeight += height;
			if (currentHeight > scrollTop) {
				break;
			}
		}

		let end = 0;
		currentHeight = -offsetHeight;
		for (end = 0; end < originalList.length; end++) {
			const item = originalList[end];
			const aspectRatio = item.height / item.width;
			const height = aspectRatio * contentSize.width + 20 + expandHeight;
			currentHeight += height;
			if (currentHeight > scrollTop + clientHeight) {
				break;
			}
		}

		if (start === range.start && end === range.end) {
			return;
		}
		setRange({ start, end });
	});

	const debounceGetRange = useMemoizedFn(debounce(getRange, 500));

	useEventListener(
		'scroll',
		(event) => {
			const target = event.target as HTMLElement;
			getRange(target);
		},
		{ target: scrollContainerRef },
	);

	useLayoutEffect(() => {
		if (scrollContainerRef && (scrollContainerRef as RefObject<HTMLDivElement>).current) {
			debounceGetRange(
				(scrollContainerRef as RefObject<HTMLDivElement>)?.current as HTMLDivElement,
			);
		} else {
			debounceGetRange(scrollContainerRef as HTMLDivElement);
		}
	}, [contentSize?.width]);

	const getCheckAspect = (item: { width: number; height: number }) => {
		const originAspect = item?.width / item?.height;
		const newHeight = renderedColumnWidth / originAspect;
		return renderedColumnWidth / (newHeight + expandHeight);
	};

	return (
		<div style={style} className={className} ref={contentRef}>
			{originalList.map((item, index) => {
				const { start, end } = range;
				const isShow = index >= start && index <= end;
				return (
					<div key={index} style={{ aspectRatio: getCheckAspect(item) }}>
						{isShow && itemRender(item, index)}
					</div>
				);
			})}
		</div>
	);
}
